import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { MODULES } from '@/utils/permissions.js';
import { useAuth } from '@/contexts/AuthContext';

interface Category {
  id: string;
  name: string;
  parentId: string | null;
  parentName: string | null;
  itemCount: number;
  isActive: boolean;
  defaultHsnCode?: string | null;
  defaultGstRate?: number | null;
}

interface Unit {
  id: string;
  name: string;
  shortName: string;
  isActive: boolean;
  itemCount: number;
  category: UnitCategory;
  isBase: boolean;
  factor: number;
  allowDecimal: boolean;
  uqcCode: string | null;
  baseUnitCode: string | null;
  inUse: boolean;
}

type UnitCategory = 'WEIGHT' | 'LENGTH' | 'VOLUME' | 'COUNT' | 'AREA' | 'TIME' | 'OTHER';

const UNIT_CATEGORIES: { value: UnitCategory; label: string }[] = [
  { value: 'WEIGHT', label: 'Weight' },
  { value: 'LENGTH', label: 'Length' },
  { value: 'VOLUME', label: 'Volume' },
  { value: 'COUNT', label: 'Count' },
  { value: 'AREA', label: 'Area' },
  { value: 'TIME', label: 'Time' },
  { value: 'OTHER', label: 'Other (no fixed conversion)' },
];
const categoryLabel = (c: string) => UNIT_CATEGORIES.find((x) => x.value === c)?.label ?? c;

// GST Unique Quantity Codes (e-invoice, e-way bill, ITC-04)
const UQC_CODES = [
  'BAG', 'BAL', 'BDL', 'BKL', 'BOU', 'BOX', 'BTL', 'BUN', 'CAN', 'CBM', 'CCM', 'CMS', 'CTN', 'DOZ', 'DRM', 'GGK',
  'GMS', 'GRS', 'GYD', 'KGS', 'KLR', 'KME', 'LTR', 'MLT', 'MTR', 'MTS', 'NOS', 'OTH', 'PAC', 'PCS', 'PRS', 'QTL',
  'ROL', 'SET', 'SQF', 'SQM', 'SQY', 'TBS', 'TGM', 'THD', 'TON', 'TUB', 'UGS', 'UNT', 'YDS',
];

const GST_RATES = [0, 5, 12, 18, 28];

const formatFactor = (n: number) => String(Number(Number(n).toFixed(8)));

type UnitFormState = {
  name: string;
  shortName: string;
  category: UnitCategory;
  isBase: boolean;
  factor: string;
  allowDecimal: boolean;
  uqcCode: string;
  isActive: boolean;
};

const emptyUnitForm = (): UnitFormState => ({
  name: '',
  shortName: '',
  category: 'COUNT',
  isBase: false,
  factor: '',
  allowDecimal: false,
  uqcCode: '',
  isActive: true,
});

type Tab = 'categories' | 'units';

// ─── API base ─────────────────────────────────────────────────
const API = `${import.meta.env.VITE_API_URL}/api/v1`;

async function apiFetch(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string>),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Request failed');
  return data;
}

export default function CategoriesUnitsPage() {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('categories');
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  // ─── Categories state ─────────────────────────────────────────
  const [categories, setCategories] = useState<Category[]>([]);
  const [catLoading, setCatLoading] = useState(false);
  const [catModal, setCatModal] = useState(false);
  const [catEditing, setCatEditing] = useState<Category | null>(null);
  const [catForm, setCatForm] = useState({ name: '', parentId: '', defaultHsnCode: '', defaultGstRate: '' });
  const [catErrors, setCatErrors] = useState<Record<string, string>>({});
  const [catDelete, setCatDelete] = useState<Category | null>(null);
  const [catSearch, setCatSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { hasPermission } = useAuth();
  const canCreateCategory = hasPermission(MODULES.CATEGORIES, 'create');
  const canEditCategory = hasPermission(MODULES.CATEGORIES, 'edit');
  const canDeleteCategory = hasPermission(MODULES.CATEGORIES, 'delete');
  const normalizedCatSearch = catSearch.trim().toLowerCase();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(catSearch);
    }, 400);

    return () => clearTimeout(timer);
  }, [catSearch]);

  const fetchCategories = useCallback(async () => {
    setCatLoading(true);

    try {
      const data = await apiFetch(
        `/categories/all?search=${encodeURIComponent(debouncedSearch)}`,
      );

      setCategories(data.data);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to fetch categories';

      error(message);
    } finally {
      setCatLoading(false);
    }
  }, [debouncedSearch, error]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const openAddCat = () => {
    setCatForm({ name: '', parentId: '', defaultHsnCode: '', defaultGstRate: '' });
    setCatErrors({});
    setCatEditing(null);
    setCatModal(true);
  };

  const openEditCat = (c: Category) => {
    setCatForm({
      name: c.name,
      parentId: c.parentId ?? '',
      defaultHsnCode: c.defaultHsnCode ?? '',
      defaultGstRate: c.defaultGstRate === null || c.defaultGstRate === undefined ? '' : String(c.defaultGstRate),
    });
    setCatErrors({});
    setCatEditing(c);
    setCatModal(true);
  };

  const saveCat = async () => {
    const e: Record<string, string> = {};
    if (!catForm.name.trim()) e.name = 'Category name is required';
    const normalizedName = catForm.name.trim().toLowerCase();
    const normalizedParentId = catForm.parentId || null;

    const duplicateCategory = categories.find((c) => {
      const sameName = c.name.trim().toLowerCase() === normalizedName;
      const sameParent = (c.parentId || null) === normalizedParentId;

      // Ignore current category while editing
      const notCurrent = c.id !== catEditing?.id;

      return sameName && sameParent && notCurrent;
    });

    if (duplicateCategory) {
      e.name = 'Category with this name already exists';
    }
    if (catForm.defaultHsnCode && ![4, 6, 8].includes(catForm.defaultHsnCode.length)) {
      e.defaultHsnCode = 'HSN must be 4, 6 or 8 digits';
    }

    if (Object.keys(e).length > 0) {
      setCatErrors(e);
      return;
    }
    try {
      if (catEditing) {
        await apiFetch(`/categories/update/${catEditing.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: catForm.name,
            parentId: catForm.parentId || null,
            defaultHsnCode: catForm.defaultHsnCode || null,
            defaultGstRate: catForm.defaultGstRate === '' ? null : Number(catForm.defaultGstRate),
          }),
        });
        success('Category updated');
      } else {
        await apiFetch('/categories/create', {
          method: 'POST',
          body: JSON.stringify({
            name: catForm.name,
            parentId: catForm.parentId || null,
            defaultHsnCode: catForm.defaultHsnCode || null,
            defaultGstRate: catForm.defaultGstRate === '' ? null : Number(catForm.defaultGstRate),
          }),
        });
        success('Category added');
      }
      setCatModal(false);
      fetchCategories();
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to save category';
      error(message);
    }
  };

  const deleteCat = async () => {
    if (!catDelete) return;
    try {
      await apiFetch(`/categories/delete/${catDelete.id}`, {
        method: 'DELETE',
      });
      success('Category removed');
      setCatDelete(null);
      fetchCategories();
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to delete category';
      error(message);
      setCatDelete(null);
    }
  };

  const rootCats = categories.filter((c) => !c.parentId);
  const childCats = (parentId: string) =>
    categories.filter((c) => c.parentId === parentId);
  const visibleRootCats = rootCats;

  const visibleChildCats = (parentId: string) => childCats(parentId);

  const toggleCategoryChildren = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // ─── Units state ──────────────────────────────────────────────
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitLoading, setUnitLoading] = useState(false);
  const [unitModal, setUnitModal] = useState(false);
  const [unitEditing, setUnitEditing] = useState<Unit | null>(null);
  const [unitForm, setUnitForm] = useState<UnitFormState>(emptyUnitForm());
  const [unitErrors, setUnitErrors] = useState<Record<string, string>>({});
  const [unitDelete, setUnitDelete] = useState<Unit | null>(null);
  const [unitSaving, setUnitSaving] = useState(false);

  const fetchUnits = useCallback(async () => {
    setUnitLoading(true);
    try {
      const data = await apiFetch('/unit/all');
      setUnits(data.data);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to fetch units';
      error(message);
    } finally {
      setUnitLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  // Units grouped by category, in the order of UNIT_CATEGORIES
  const unitGroups = UNIT_CATEGORIES.map((c) => ({
    ...c,
    units: units.filter((u) => u.category === c.value),
  })).filter((g) => g.units.length > 0);

  // Base unit of the category picked in the form (other than the unit being edited)
  const formBaseUnit = units.find(
    (u) => u.category === unitForm.category && u.isBase && u.id !== unitEditing?.id,
  );
  const formConvertible = unitForm.category !== 'OTHER';
  // A convertible category without a base can only get its base unit first
  const mustBeBase = formConvertible && !formBaseUnit;
  const unitLocked = Boolean(unitEditing?.inUse);

  const setUnitField = <K extends keyof UnitFormState>(key: K, value: UnitFormState[K]) => {
    setUnitForm((p) => ({ ...p, [key]: value }));
    setUnitErrors((e) => {
      if (!e[key]) return e;
      const n = { ...e };
      delete n[key];
      return n;
    });
  };

  const openAddUnit = () => {
    setUnitForm(emptyUnitForm());
    setUnitErrors({});
    setUnitEditing(null);
    setUnitModal(true);
  };

  const openEditUnit = (u: Unit) => {
    setUnitForm({
      name: u.name,
      shortName: u.shortName,
      category: u.category,
      isBase: u.isBase,
      factor: formatFactor(u.factor),
      allowDecimal: u.allowDecimal,
      uqcCode: u.uqcCode ?? '',
      isActive: u.isActive,
    });
    setUnitErrors({});
    setUnitEditing(u);
    setUnitModal(true);
  };

  const saveUnit = async () => {
    if (unitSaving) return;
    const isBase = formConvertible && (unitForm.isBase || mustBeBase);
    const factor = isBase || !formConvertible ? 1 : Number(unitForm.factor);
    const code = unitForm.shortName.trim();

    const e: Record<string, string> = {};
    if (!unitForm.name.trim()) e.name = 'Unit name is required';
    if (!code) e.shortName = 'Short code is required';
    else if (units.some((u) => u.id !== unitEditing?.id && u.shortName.toLowerCase() === code.toLowerCase())) {
      e.shortName = `Code "${code}" is already used`;
    }
    if (!Number.isFinite(factor) || factor <= 0) e.factor = 'Factor must be greater than 0';
    if (Object.keys(e).length > 0) {
      setUnitErrors(e);
      return;
    }

    const payload = {
      name: unitForm.name.trim(),
      shortName: code,
      category: unitForm.category,
      isBase,
      factor,
      allowDecimal: unitForm.allowDecimal,
      uqcCode: unitForm.uqcCode || null,
      isActive: unitForm.isActive,
    };

    setUnitSaving(true);
    try {
      if (unitEditing) {
        await apiFetch(`/unit/update/${unitEditing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        success('Unit updated');
      } else {
        await apiFetch('/unit/create', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        success('Unit added');
      }
      setUnitModal(false);
      fetchUnits();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to save unit';
      error(message);
    } finally {
      setUnitSaving(false);
    }
  };

  const toggleUnit = async (u: Unit) => {
    try {
      await apiFetch(`/unit/${u.id}/toggle`, { method: 'PATCH' });
      fetchUnits();
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to toggle unit status';
      error(message);
    }
  };

  const deleteUnit = async () => {
    if (!unitDelete) return;
    try {
      await apiFetch(`/unit/delete/${unitDelete.id}`, { method: 'DELETE' });
      success('Unit removed');
      setUnitDelete(null);
      fetchUnits();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to delete unit';
      error(message);
      setUnitDelete(null);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-5 w-full ">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-[#64748b] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-[#1e293b]">
                Categories &amp; Units
              </h2>
              <p className="text-sm text-[#64748b] mt-0.5">
                Organize items with hierarchical categories and measurement
                units
              </p>
            </div>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 bg-[#f1f5f9] rounded-lg p-1 w-fit">
          {(['categories', 'units'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`h-8 px-5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap capitalize ${
                activeTab === t
                  ? 'bg-white text-[#1e293b] shadow-sm'
                  : 'text-[#64748b] hover:text-[#1e293b]'
              }`}
            >
              {t === 'categories'
                ? `Categories (${categories.length})`
                : `Units (${units.length})`}
            </button>
          ))}
        </div>

        {/* ── Categories Tab ──────────────────────────────────── */}
        {activeTab === 'categories' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm" />
                <input
                  type="text"
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  placeholder="Search categories..."
                  className="w-full h-9 pl-9 pr-4 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                />
              </div>
              {canCreateCategory && (
                <button
                  onClick={openAddCat}
                  className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" /> Add Category
                </button>
              )}
            </div>

            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
              {catLoading ? (
                <div className="px-4 py-10 text-center text-[#94a3b8] text-sm">
                  <i className="ri-loader-4-line animate-spin text-xl block mb-2" />
                  Loading categories...
                </div>
              ) : visibleRootCats.length === 0 ? (
                <div className="px-4 py-10 text-center text-[#94a3b8] text-sm">
                  No categories found
                </div>
              ) : (
                visibleRootCats.map((cat) => {
                  const filteredChildren = visibleChildCats(cat.id);
                  const shouldExpand =
                    expandedCategories[cat.id] ||
                    normalizedCatSearch.length > 0;

                  return (
                    <div key={cat.id}>
                      {/* Root category */}
                      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#f1f5f9] hover:bg-[#f8fafc] group">
                        <button
                          type="button"
                          onClick={() => toggleCategoryChildren(cat.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50 hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer shrink-0"
                          aria-label={
                            shouldExpand
                              ? 'Hide subcategories'
                              : 'Show subcategories'
                          }
                          title={
                            shouldExpand
                              ? 'Hide subcategories'
                              : 'Show subcategories'
                          }
                        >
                          <i
                            className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                              shouldExpand ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 shrink-0">
                          <i className="ri-folder-line text-[#4f46e5] text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#1e293b] text-sm">
                            {cat.name}
                          </p>
                          <p className="text-xs text-[#94a3b8]">
                            {cat.itemCount} items · {filteredChildren.length}{' '}
                            sub-categories
                            {cat.defaultHsnCode && ` · HSN ${cat.defaultHsnCode}`}
                            {cat.defaultGstRate !== null && cat.defaultGstRate !== undefined && ` · GST ${cat.defaultGstRate}%`}
                          </p>
                        </div>
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            cat.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {cat.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canEditCategory && (
                            <button
                              onClick={() => openEditCat(cat)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-line text-sm" />
                            </button>
                          )}
                          {canDeleteCategory && (
                            <button
                              onClick={() => setCatDelete(cat)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Sub-categories */}
                      {shouldExpand &&
                        filteredChildren.map((child) => (
                          <div
                            key={child.id}
                            className="flex items-center gap-3 px-4 py-2.5 border-b border-[#f1f5f9] hover:bg-[#f8fafc] group pl-16 bg-[#fcfcfd]"
                          >
                            <div className="w-1 h-1 rounded-full bg-[#cbd5e1] shrink-0 mr-1" />
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50 shrink-0">
                              <i className="ri-folder-2-line text-[#94a3b8] text-sm" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-[#1e293b] text-sm">
                                {child.name}
                              </p>
                              <p className="text-xs text-[#94a3b8]">
                                {child.itemCount} items
                                {child.defaultHsnCode && ` · HSN ${child.defaultHsnCode}`}
                                {child.defaultGstRate !== null && child.defaultGstRate !== undefined && ` · GST ${child.defaultGstRate}%`}
                              </p>
                            </div>
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                child.isActive
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {child.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {canEditCategory && (
                                <button
                                  onClick={() => openEditCat(child)}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer"
                                >
                                  <i className="ri-edit-line text-sm" />
                                </button>
                              )}
                              {canDeleteCategory && (
                                <button
                                  onClick={() => setCatDelete(child)}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line text-sm" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── Units Tab ────────────────────────────────────────── */}
        {activeTab === 'units' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-[#64748b]">
                Every unit belongs to a category and converts to that category&apos;s base unit.
                Conversion happens only within a category.
              </p>
              {canCreateCategory && (
                <button
                  onClick={openAddUnit}
                  className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-add-line" /> Add Unit
                </button>
              )}
            </div>
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-x-auto">
              {unitLoading ? (
                <div className="px-4 py-10 text-center text-[#94a3b8] text-sm">
                  <i className="ri-loader-4-line animate-spin text-xl block mb-2" />
                  Loading units...
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                      {[
                        'Unit Name',
                        'Code',
                        'Conversion',
                        'Decimal',
                        'UQC',
                        'Items Using',
                        'Status',
                        '',
                      ].map((h) => (
                        <th
                          key={h}
                          className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {units.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-10 text-center text-[#94a3b8] text-sm"
                        >
                          No units found
                        </td>
                      </tr>
                    ) : (
                      unitGroups.map((group) => (
                        <UnitGroupRows
                          key={group.value}
                          label={group.label}
                          units={group.units}
                          canEdit={canEditCategory}
                          canDelete={canDeleteCategory}
                          onEdit={openEditUnit}
                          onToggle={toggleUnit}
                          onDelete={setUnitDelete}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Category Modal */}
      {catModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setCatModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-[#1e293b]">
              {catEditing ? 'Edit Category' : 'Add Category'}
            </h3>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={catForm.name}
                onChange={(e) =>
                  setCatForm((p) => ({ ...p, name: e.target.value }))
                }
                className={`w-full h-10 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 transition-colors ${
                  catErrors.name
                    ? 'border-red-400 focus:ring-red-200'
                    : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                }`}
                placeholder="Electronics"
                autoFocus
              />
              {catErrors.name && (
                <p className="text-xs text-red-500">{catErrors.name}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Parent Category (optional)
              </label>
              <select
                value={catForm.parentId}
                onChange={(e) =>
                  setCatForm((p) => ({ ...p, parentId: e.target.value }))
                }
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] cursor-pointer"
              >
                <option value="">None (Root Category)</option>
                {categories
                  .filter((c) => !c.parentId && c.id !== catEditing?.id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Default HSN
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={catForm.defaultHsnCode}
                  onChange={(e) =>
                    setCatForm((p) => ({ ...p, defaultHsnCode: e.target.value.replace(/\D/g, '').slice(0, 8) }))
                  }
                  className={`w-full h-10 px-3 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 ${
                    catErrors.defaultHsnCode
                      ? 'border-red-400 focus:ring-red-200'
                      : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                  }`}
                  placeholder="Optional"
                />
                {catErrors.defaultHsnCode && (
                  <p className="text-xs text-red-500">{catErrors.defaultHsnCode}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Default GST
                </label>
                <select
                  value={catForm.defaultGstRate}
                  onChange={(e) =>
                    setCatForm((p) => ({ ...p, defaultGstRate: e.target.value }))
                  }
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] cursor-pointer"
                >
                  <option value="">None</option>
                  {GST_RATES.map((r) => (
                    <option key={r} value={String(r)}>
                      {r}%
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-[#94a3b8] -mt-1">
              Filled in automatically when a new item is created in this category.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setCatModal(false)}
                className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={saveCat}
                className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-save-line" /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unit Modal */}
      {unitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setUnitModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-[#1e293b]">
              {unitEditing ? 'Edit Unit' : 'Add Unit'}
            </h3>
            {unitLocked && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <i className="ri-lock-line mt-0.5" />
                <span>
                  This unit is used by items or transactions, so its category, base flag and
                  conversion factor cannot be changed.
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Unit Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={unitForm.name}
                  onChange={(e) => setUnitField('name', e.target.value)}
                  className={`w-full h-10 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 disabled:bg-[#f8fafc] disabled:text-[#94a3b8] disabled:cursor-not-allowed ${
                    unitErrors.name
                      ? 'border-red-400 focus:ring-red-200'
                      : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                  }`}
                  placeholder="Gram"
                  autoFocus
                />
                {unitErrors.name && (
                  <p className="text-xs text-red-500">{unitErrors.name}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Short Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={unitForm.shortName}
                  onChange={(e) => setUnitField('shortName', e.target.value)}
                  maxLength={10}
                  className={`font-mono ${`w-full h-10 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 disabled:bg-[#f8fafc] disabled:text-[#94a3b8] disabled:cursor-not-allowed ${
                    unitErrors.shortName
                      ? 'border-red-400 focus:ring-red-200'
                      : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                  }`}`}
                  placeholder="g"
                />
                {unitErrors.shortName && (
                  <p className="text-xs text-red-500">{unitErrors.shortName}</p>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={unitForm.category}
                disabled={unitLocked}
                onChange={(e) => {
                  const category = e.target.value as UnitCategory;
                  setUnitForm((p) => ({ ...p, category, isBase: false }));
                }}
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] cursor-pointer disabled:bg-[#f8fafc] disabled:text-[#94a3b8] disabled:cursor-not-allowed"
              >
                {UNIT_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
              {!formConvertible && (
                <p className="text-xs text-[#94a3b8]">
                  Units like Box or Set hold a different quantity for every item, so they never
                  convert to another unit.
                </p>
              )}
            </div>
            {formConvertible && (
              <>
                <label className={`flex items-center gap-2 text-sm text-[#1e293b] ${unitLocked || mustBeBase || (!!formBaseUnit && !unitForm.isBase) ? 'opacity-60' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={unitForm.isBase || mustBeBase}
                    disabled={unitLocked || mustBeBase || !!formBaseUnit}
                    onChange={(e) => setUnitField('isBase', e.target.checked)}
                    className="w-4 h-4 accent-[#4f46e5]"
                  />
                  Base unit of {categoryLabel(unitForm.category)}
                  <span className="text-xs text-[#94a3b8]">
                    {mustBeBase
                      ? '(no base unit yet, this becomes the base)'
                      : formBaseUnit
                        ? `(base is ${formBaseUnit.shortName})`
                        : ''}
                  </span>
                </label>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                    Conversion Factor <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={unitForm.isBase || mustBeBase ? '1' : unitForm.factor}
                    disabled={unitLocked || unitForm.isBase || mustBeBase}
                    onChange={(e) => setUnitField('factor', e.target.value)}
                    className={`font-mono ${`w-full h-10 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 disabled:bg-[#f8fafc] disabled:text-[#94a3b8] disabled:cursor-not-allowed ${
                    unitErrors.factor
                      ? 'border-red-400 focus:ring-red-200'
                      : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                  }`}`}
                    placeholder="0.001"
                  />
                  {unitErrors.factor ? (
                    <p className="text-xs text-red-500">{unitErrors.factor}</p>
                  ) : (
                    <p className="text-xs text-[#64748b]">
                      {unitForm.isBase || mustBeBase
                        ? 'A base unit is always 1.'
                        : formBaseUnit && Number(unitForm.factor) > 0
                          ? `Preview: 1 ${unitForm.shortName.trim() || 'unit'} = ${formatFactor(Number(unitForm.factor))} ${formBaseUnit.shortName}`
                          : `How many ${formBaseUnit?.shortName ?? 'base units'} make 1 of this unit.`}
                    </p>
                  )}
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  GST UQC
                </label>
                <select
                  value={unitForm.uqcCode}
                  onChange={(e) => setUnitField('uqcCode', e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] cursor-pointer"
                >
                  <option value="">Not set</option>
                  {UQC_CODES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 pt-6">
                <label className="flex items-center gap-2 text-sm text-[#1e293b] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unitForm.allowDecimal}
                    onChange={(e) => setUnitField('allowDecimal', e.target.checked)}
                    className="w-4 h-4 accent-[#4f46e5]"
                  />
                  Decimal allowed
                </label>
                <label className="flex items-center gap-2 text-sm text-[#1e293b] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unitForm.isActive}
                    onChange={(e) => setUnitField('isActive', e.target.checked)}
                    className="w-4 h-4 accent-[#4f46e5]"
                  />
                  Active
                </label>
              </div>
            </div>
            <p className="text-xs text-[#94a3b8] -mt-1">
              Turn decimals off for units counted whole (Pcs, Nos); keep them on for Kg, L, m.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setUnitModal(false)}
                className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={saveUnit}
                disabled={unitSaving}
                className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                <i className={unitSaving ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!catDelete}
        title="Remove Category"
        message={`Remove "${catDelete?.name}"? Sub-categories will remain.`}
        variant="danger"
        confirmLabel="Yes (Y)"
        onConfirm={deleteCat}
        onCancel={() => setCatDelete(null)}
      />
      <ConfirmDialog
        open={!!unitDelete}
        title="Remove Unit"
        message={`Remove unit "${unitDelete?.name}"?`}
        variant="danger"
        confirmLabel="Yes (Y)"
        onConfirm={deleteUnit}
        onCancel={() => setUnitDelete(null)}
      />
    </AppLayout>
  );
}

function UnitGroupRows({
  label,
  units,
  canEdit,
  canDelete,
  onEdit,
  onToggle,
  onDelete,
}: {
  label: string;
  units: Unit[];
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (u: Unit) => void;
  onToggle: (u: Unit) => void;
  onDelete: (u: Unit) => void;
}) {
  const base = units.find((u) => u.isBase);
  return (
    <>
      <tr className="bg-[#f1f5f9]/70 border-b border-[#e2e8f0]">
        <td colSpan={8} className="px-4 py-2 text-xs font-semibold text-[#475569] uppercase tracking-wide">
          {label}
          {base && (
            <span className="ml-2 normal-case font-normal text-[#94a3b8]">
              base unit: {base.shortName}
            </span>
          )}
        </td>
      </tr>
      {units.map((unit) => (
        <tr
          key={unit.id}
          className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors"
        >
          <td className="px-4 py-3 font-medium text-[#1e293b] whitespace-nowrap">
            {unit.name}
            {unit.isBase && (
              <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-[#4f46e5] uppercase">
                Base
              </span>
            )}
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="font-mono text-xs bg-[#f1f5f9] px-2 py-1 rounded font-semibold text-[#1e293b]">
              {unit.shortName}
            </span>
          </td>
          <td className="px-4 py-3 text-[#64748b] whitespace-nowrap font-mono text-xs">
            {unit.category === 'OTHER'
              ? <span className="font-sans text-[#94a3b8]">No fixed conversion</span>
              : unit.isBase
                ? `1 ${unit.shortName}`
                : `1 ${unit.shortName} = ${formatFactor(unit.factor)} ${unit.baseUnitCode ?? ''}`}
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <i className={unit.allowDecimal ? 'ri-check-line text-green-600' : 'ri-close-line text-[#94a3b8]'} />
          </td>
          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-[#64748b]">
            {unit.uqcCode || '—'}
          </td>
          <td className="px-4 py-3 text-[#64748b] whitespace-nowrap">
            {unit.itemCount} items
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <button onClick={() => onToggle(unit)} className="cursor-pointer">
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  unit.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                {unit.isActive ? 'Active' : 'Inactive'}
              </span>
            </button>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="flex items-center gap-1">
              {unit.inUse && (
                <i
                  className="ri-lock-line text-[#94a3b8] text-sm mr-1"
                  title="In use: category and factor are locked"
                />
              )}
              {canEdit && (
                <button
                  onClick={() => onEdit(unit)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer"
                >
                  <i className="ri-edit-line text-sm" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => onDelete(unit)}
                  disabled={unit.inUse}
                  title={unit.inUse ? 'In use: mark it Inactive instead' : ''}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}
